import { writable, get } from 'svelte/store'

export interface DemoState {
  mode: 'demo' | 'free' | 'pro' | 'beta' | 'dev'
  limits: {
    spellCheck: {
      used: number
      max: number
      lastUsed: string | null
    }
    ai: {
      used: number
      max: number
      breakdown: {
        rewrite: number
        expand: number
        summarize: number
        translate: number
      }
      lastUsed: string | null
    }
  }
  deviceId: string
  firstUsedAt: string
  lastResetAt: string | null
  conversionAttempts: number
  hasExtended: boolean
}

export interface CheckResult {
  success: boolean
  remaining: number
  reason?: 'limit_exceeded'
  showWarning?: boolean
}

const DEMO_STORAGE_KEY = 'kg-demo-v2'
const DEVICE_ID_KEY = 'kg-device-id'

// Initial state
const initialState: DemoState = {
  mode: 'demo',
  limits: {
    spellCheck: {
      used: 0,
      max: 5,
      lastUsed: null
    },
    ai: {
      used: 0,
      max: 5,
      breakdown: {
        rewrite: 0,
        expand: 0,
        summarize: 0,
        translate: 0
      },
      lastUsed: null
    }
  },
  deviceId: '',
  firstUsedAt: new Date().toISOString(),
  lastResetAt: null,
  conversionAttempts: 0,
  hasExtended: false
}

// Create store
const demoStore = writable<DemoState>(initialState)

// Demo Tracker Class
export class DemoTracker {
  private static instance: DemoTracker | null = null

  private constructor() {
    this.initializeState()
  }

  static getInstance(): DemoTracker {
    if (!DemoTracker.instance) {
      DemoTracker.instance = new DemoTracker()
    }
    return DemoTracker.instance
  }

  // Generate device fingerprint
  private generateDeviceId(): string {
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return 'default-id'

      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.fillText('🇰🇷', 0, 0)
      const dataURL = canvas.toDataURL()

      const fingerprint = [
        navigator.userAgent,
        navigator.language,
        screen.width + 'x' + screen.height,
        new Date().getTimezoneOffset().toString(),
        dataURL.slice(-50) // Use last 50 chars of canvas data
      ].join('|')

      // Create a simple hash
      let hash = 0
      for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash // Convert to 32bit integer
      }

      return Math.abs(hash).toString(36)
    } catch (error) {
      console.error('Failed to generate device ID:', error)
      return 'fallback-' + Date.now().toString(36)
    }
  }

  // Initialize or restore state
  private initializeState(): void {
    try {
      // Try to restore from localStorage
      const savedState = localStorage.getItem(DEMO_STORAGE_KEY)

      if (savedState) {
        const parsed = JSON.parse(savedState) as DemoState
        // Validate the saved state has all required fields
        if (this.isValidState(parsed)) {
          demoStore.set(parsed)
          return
        }
      }

      // No valid saved state, create new one
      const newState = { ...initialState }
      newState.deviceId = this.generateDeviceId()
      newState.firstUsedAt = new Date().toISOString()

      demoStore.set(newState)
      this.saveState(newState)
    } catch (error) {
      console.error('Failed to initialize demo state:', error)
      // Fall back to initial state
      const newState = { ...initialState }
      newState.deviceId = this.generateDeviceId()
      demoStore.set(newState)
    }
  }

  // Validate state structure
  private isValidState(state: any): state is DemoState {
    return state
      && typeof state.mode === 'string'
      && state.limits
      && state.limits.spellCheck
      && state.limits.ai
      && typeof state.limits.spellCheck.used === 'number'
      && typeof state.limits.ai.used === 'number'
  }

  // Save state to localStorage
  private saveState(state: DemoState): void {
    try {
      localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(state))

      // Also save device ID separately for cross-checking
      localStorage.setItem(DEVICE_ID_KEY, state.deviceId)
    } catch (error) {
      console.error('Failed to save demo state:', error)
    }
  }

  // Get current state
  getState(): DemoState {
    return get(demoStore)
  }

  // Check if spell check is available
  canUseSpellCheck(): boolean {
    const state = this.getState()
    return state.limits.spellCheck.used < state.limits.spellCheck.max
  }

  // Check if AI features are available
  canUseAI(): boolean {
    const state = this.getState()
    return state.limits.ai.used < state.limits.ai.max
  }

  // Use spell check
  useSpellCheck(): CheckResult {
    const state = this.getState()

    if (!this.canUseSpellCheck()) {
      return {
        success: false,
        remaining: 0,
        reason: 'limit_exceeded'
      }
    }

    // Update state
    state.limits.spellCheck.used++
    state.limits.spellCheck.lastUsed = new Date().toISOString()

    // Save and update store
    this.saveState(state)
    demoStore.set(state)

    const remaining = state.limits.spellCheck.max - state.limits.spellCheck.used

    return {
      success: true,
      remaining,
      showWarning: remaining <= 2 // Show warning when 2 or less remaining
    }
  }

  // Use AI feature
  useAI(feature: 'rewrite' | 'expand' | 'summarize' | 'translate'): CheckResult {
    const state = this.getState()

    if (!this.canUseAI()) {
      return {
        success: false,
        remaining: 0,
        reason: 'limit_exceeded'
      }
    }

    // Update state
    state.limits.ai.used++
    state.limits.ai.breakdown[feature]++
    state.limits.ai.lastUsed = new Date().toISOString()

    // Save and update store
    this.saveState(state)
    demoStore.set(state)

    const remaining = state.limits.ai.max - state.limits.ai.used

    return {
      success: true,
      remaining,
      showWarning: remaining <= 2 // Show warning when 2 or less remaining
    }
  }

  // Get remaining counts
  getRemaining(): { spellCheck: number; ai: number; total: number } {
    const state = this.getState()
    const spellCheck = state.limits.spellCheck.max - state.limits.spellCheck.used
    const ai = state.limits.ai.max - state.limits.ai.used

    return {
      spellCheck: Math.max(0, spellCheck),
      ai: Math.max(0, ai),
      total: Math.max(0, spellCheck + ai)
    }
  }

  // Extend demo (one-time only)
  extendDemo(amount: number = 2): boolean {
    const state = this.getState()

    if (state.hasExtended) {
      return false
    }

    // Add to both limits
    state.limits.spellCheck.max += amount
    state.limits.ai.max += amount
    state.hasExtended = true
    state.lastResetAt = new Date().toISOString()

    // Save and update store
    this.saveState(state)
    demoStore.set(state)

    return true
  }

  // Reset demo (for testing)
  resetDemo(): void {
    const newState = { ...initialState }
    newState.deviceId = this.generateDeviceId()
    newState.firstUsedAt = new Date().toISOString()

    demoStore.set(newState)
    this.saveState(newState)
  }

  // Check if in demo mode
  isDemoMode(): boolean {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('demo') === 'true'
  }
}

// Export store and singleton instance
export { demoStore }
export const demoTracker = DemoTracker.getInstance()

// Helper functions for components
export function getDemoRemaining() {
  return demoTracker.getRemaining()
}

export function canUseFeature(type: 'spellCheck' | 'ai'): boolean {
  if (type === 'spellCheck') {
    return demoTracker.canUseSpellCheck()
  }
  return demoTracker.canUseAI()
}